import { EditOutlined } from '@ant-design/icons';
import {
  Button,
  Drawer,
  Popconfirm,
  Select,
  Space,
  Tooltip,
  message,
} from 'antd';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { PaymentInfoTables } from 'components/PaymentsInfoTables';
import { FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PaymentsService } from 'services/PaymentsService';
import { fetchPackages } from 'store/reducers/staticHandbooksReducer';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { updateStatuses } from 'utils/handbooks/paymentsHandbook';
import { ClientPaymentsForm } from './ClientPaymentsForm';
import { ClientPaymentsTable } from './ClientPaymentsTable';
import { ExposePaymentDrawer } from './ExposePaymentDrawer';

const PAGE_TITLE = 'Выставленные клиенту платежи';
const canUpdateStatuses = ['created', 'on_payment', 'paid_another'];

function ClientPayments() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const dispatch = useDispatch();
  const { packagesData } = useSelector((state) => state.staticHandbooks);
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [issuePaymentLoading, setIssuePaymentLoading] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(null);
  const tribeId = useTribe();
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const {
    state: { formIsActive, drawerVisible, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    closeForm,
    hideDrawer,
  } = useDrawers();
  const initialPaymentsInfo = {
    cardPayments: null,
    clientsPayments: null,
    bccPayments: null,
  };
  const [paymentsInfo, setPaymentsInfo] = useState(initialPaymentsInfo);
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    status: [],
    period: null,
    type: null,
    f_created: null,
    t_created: null,
  });
  const { client_id: selectedClient } = filters;

  const canIssueInvoice = useMemo(() => {
    if (selectedPayments.length > 0) {
      return selectedPayments.every((payment) => payment.status === 'created');
    }

    return false;
  }, [selectedPayments]);

  const checkPaymentsStatuses = (record) => {
    if (!record) {
      return;
    }

    return (
      record.type !== 'service' && canUpdateStatuses.includes(record.status)
    );
  };

  const canUpdateMultiple = useMemo(() => {
    if (selectedPayments.length) {
      return selectedPayments.every(checkPaymentsStatuses);
    }
  }, [selectedPayments]);

  const canEditPayment = useMemo(() => {
    return checkPaymentsStatuses(activeRecord);
  }, [activeRecord]);

  const fetchClientPayments = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await PaymentsService.getClientPayments(
        page,
        perPage,
        filters,
        tribeId
      );

      setPayments(items);
      setTotalRows(total);
      setCount(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  useEffect(() => fetchClientPayments(), [fetchClientPayments]);

  useEffect(() => {
    if (activeRecord) {
      fetchPaymentsInfo();
    }
  }, [activeRecord]);

  useEffect(() => {
    if (packagesData.items.length === 0) {
      dispatch(fetchPackages());
    }
  }, []);

  const fetchPaymentsInfo = async () => {
    setLoading(true);

    try {
      const { card_payment, payment, bcc } =
        await PaymentsService.getClientPaymentsDetail(activeRecord?.id);

      setPaymentsInfo({
        cardPayments: card_payment,
        clientsPayments: payment,
        bccPayments: bcc,
      });
    } catch (err) {
      setPaymentsInfo(initialPaymentsInfo);
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleIssueInvoice = async () => {
    const hide = message.loading(REQUEST_LOADING);
    setIssuePaymentLoading(true);

    try {
      const isSuccess = await PaymentsService.issueInvoice(selectedPaymentIds);

      if (isSuccess) {
        message.success('Платеж успешно выставлен');
        setIssuePaymentLoading(false);

        fetchClientPayments();
        setSelectedPayments([]);
        setSelectedPaymentIds([]);
      }
    } catch (err) {
      message.error('Не удалось выставить платеж');
      setIssuePaymentLoading(false);
    } finally {
      hide();
    }
  };

  const handlePaymentEditClick = () => {
    setEditing(true);
    hideDrawer();
  };

  const handlePaymentEditSuccess = () => {
    closeDrawer();
    setEditing(false);
    fetchClientPayments();
  };

  const handlePaymentGenerateSuccess = () => {
    closeForm();
    fetchClientPayments();
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const handleUpdateMultipleStatuses = async () => {
    const payload = {
      status,
      id: selectedPaymentIds,
    };

    try {
      const res = await PaymentsService.editClientPayment(payload);

      if (res) {
        message.success('Статус платежей успешно отредактирован');
        setStatus(null);
        fetchClientPayments();
        setSelectedPayments([]);
        setSelectedPaymentIds([]);
      }
    } catch (err) {
      message.error(err);
    }
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          clientId={clientId}
          pageTitle={PAGE_TITLE}
          onClientsChange={(clients) => setClients(clients)}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <Space align="center" size="small">
          <AccessControl pageId="paymentRecord" action="update">
            <Space align="center" size="small">
              {canUpdateMultiple ? (
                <>
                  <Select
                    allowClear
                    placeholder="Выбрать статус"
                    style={{
                      minWidth: '200px',
                    }}
                    onChange={(value) => setStatus(value)}
                  >
                    {updateStatuses.map(({ value, text }) => (
                      <Select.Option key={value} value={value}>
                        {text}
                      </Select.Option>
                    ))}
                  </Select>

                  {status ? (
                    <Popconfirm
                      title="Вы точно хотите поменять статусы платежей?"
                      placement="bottomLeft"
                      onConfirm={handleUpdateMultipleStatuses}
                      okText="Да"
                      cancelText="Нет"
                    >
                      <Button type="primary">Поменять</Button>
                    </Popconfirm>
                  ) : null}
                </>
              ) : null}

              {canIssueInvoice && (
                <Popconfirm
                  title="Вы точно хотите выставить платеж?"
                  placement="bottomLeft"
                  onConfirm={handleIssueInvoice}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button type="primary" loading={issuePaymentLoading}>
                    Выставить клиенту ({selectedPaymentIds.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </AccessControl>

          <AccessControl pageId="paymentRecord" action="create">
            <Button
              disabled={!selectedClient}
              type="primary"
              onClick={openCreateForm}
            >
              Сформировать
            </Button>
          </AccessControl>
        </Space>
      </ActionsWrapper>

      <Drawer
        title={activeRecord?.company_name}
        width="70%"
        onClose={closeDrawer}
        visible={drawerVisible}
        getContainer={false}
        extra={
          canEditPayment ? (
            <Tooltip placement="topLeft" title="Редактировать платеж">
              <Button
                onClick={handlePaymentEditClick}
                type="default"
                icon={<EditOutlined />}
              />
            </Tooltip>
          ) : null
        }
      >
        <PaymentInfoTables loading={loading} payments={paymentsInfo} />
      </Drawer>

      <ClientPaymentsTable
        allowFilter
        items={payments}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        selectedRowIds={selectedPaymentIds}
        onRowClick={openDrawer}
        onRowSelect={(selectedRowIds, selectedRows) => {
          setSelectedPaymentIds(selectedRowIds);
          setSelectedPayments(selectedRows);
        }}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />

      <ExposePaymentDrawer
        isActive={formIsActive}
        onClose={closeForm}
        onSuccess={handlePaymentGenerateSuccess}
        clientId={selectedClient}
        clients={clients}
        packages={packagesData.items}
        packagesLoading={packagesData.loading}
      />

      <ClientPaymentsForm
        isActive={editing}
        initialValues={activeRecord}
        onClose={() => setEditing(false)}
        onSuccess={handlePaymentEditSuccess}
      />
    </>
  );
}

export { ClientPayments };
